import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {CrossReferenceIcon} from '@primer/octicons-react'

import {LABELS} from '../constants/labels'
import {getGroupCreatedAt} from '../utils/get-group-created-at'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {BaseDependencyEventProps} from './BlockedByAddedEvent'
import styles from './DependencyEvent.module.css'
import type {RelatesToRemovedEventNode} from './issue-viewer-page-query-types'
import {IssueLink} from './IssueLink'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(RelatesToRemovedEvent): widens nullable fields so still-Relay parent data is assignable.
// Remove when the timeline pagination fragments leave Relay.
export type RelatesToRemovedEventFragment = RelayCompat<
  Pick<RelatesToRemovedEventNode, 'databaseId' | 'actor' | 'createdAt' | 'relatedIssue'>
>

type EventItem = RelatesToRemovedEventFragment

type RelatesToRemovedEventProps = BaseDependencyEventProps & {
  queryRef: EventItem
  rollupGroup?: Record<'RelatesToRemovedEvent', EventItem[]>
}

export function RelatesToRemovedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  repositoryId,
  ownerLogin,
}: RelatesToRemovedEventProps) {
  const {actor, createdAt, relatedIssue, databaseId} = queryRef

  if (!relatedIssue) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup?.RelatesToRemovedEvent || []
  const itemsToRender = rolledUpGroup.length === 0 ? [queryRef] : rolledUpGroup
  const eventCreatedAt = getGroupCreatedAt(queryRef.createdAt, rolledUpGroup)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      showAgoTimestamp={false}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={CrossReferenceIcon}
    >
      <TimelineRow.Main>
        <span>
          {itemsToRender.length === 1
            ? LABELS.timeline.relatesToRemoved.single
            : LABELS.timeline.relatesToRemoved.multiple(itemsToRender.length)}
        </span>{' '}
        {eventCreatedAt ? (
          <Ago timestamp={new Date(eventCreatedAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        ) : null}
      </TimelineRow.Main>
      <TimelineRow.Secondary>
        <ul className={styles.issueDependenciesList}>
          {itemsToRender.map((item, index) => (
            <RelatesToRemovedEventItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${databaseId}_${index}`}
              event={item}
              targetRepositoryId={repositoryId}
              targetOwnerLogin={ownerLogin}
            />
          ))}
        </ul>
      </TimelineRow.Secondary>
    </TimelineRow>
  )
}

function RelatesToRemovedEventItem({
  event,
  targetRepositoryId,
  targetOwnerLogin,
}: {
  event: RelatesToRemovedEventFragment
  targetRepositoryId: string
  targetOwnerLogin: string
}) {
  const {relatedIssue} = event

  if (!relatedIssue) {
    return null
  }

  return (
    <li className={styles.issueDependencyItem}>
      <IssueLink data={relatedIssue} targetRepositoryId={targetRepositoryId} targetOwnerLogin={targetOwnerLogin} />
    </li>
  )
}
